import { type FC, Suspense, useEffect } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { mobile } from 'dibs-client-check';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers.jsx';
import { scheduleHomepageReloadWhenAuthIdle } from '../homepage/helpers/scheduleHomepageReloadWhenAuthIdle.ts';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { trackPageview, trackUserEventHomepageView } from 'dibs-tracking';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { useHomePageContext } from '../homepage/HomePageContext.tsx';
import { HpSharedAppBannerLazy } from '../homepage/HpSharedAppBanner/HpSharedAppBannerLazy.tsx';
import { HpSharedCompleteEmailOptinConfirmationModalLazy } from '../homepage/HpSharedCompleteEmailOptinConfirmationModal/HpSharedCompleteEmailOptinConfirmationModalLazy.tsx';
import { defaultComponentModuleMap } from './defaultComponentModuleMap.tsx';
import { HpSharedBillboardAd } from '../homepage/HpSharedBillboardAd/HpSharedBillboardAd.tsx';
import {
    HOME_BOTTOM_BILLBOARD_PLACEMENT,
    HOME_TOP_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants.ts';
import { HpSharedFeaturedModuleLazy } from '../homepage/HpSharedFeaturedModule/HpSharedFeaturedModuleLazy.tsx';
import HpSharedSkinnyBannerModule from '../homepage/HpSharedSkinnyBannerModule/HpSharedSkinnyBannerModule.tsx';
import { HpSharedItemsGrid } from '../homepage/HpSharedItemsGrid/HpSharedItemsGrid.tsx';

import { type HpDefaultLayout_viewer$key } from './__generated__/HpDefaultLayout_viewer.graphql.ts';
import { HpNewArrivalsCarousel } from '../homepage/HpSharedRecentlyViewedItems/HpNewArrivalsCarousel.tsx';
import { HpRecentlyViewedShimmer } from '../homepage/HpSharedRecentlyViewedItems/HpRecentlyViewedShimmer.tsx';
import { HpRecentlySearchedItemsModuleLazy } from '../homepage/HpRecentlySearchedItems/HpRecentlySearchedItemsModuleLazy.tsx';

type HpDefaultLayoutProps = {
    viewer: HpDefaultLayout_viewer$key;
};

const viewerFragment = graphql`
    fragment HpDefaultLayout_viewer on Viewer {
        homepage(rootId: $homepageId, abTestList: $abTestList) {
            modules {
                ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                    ... on TopHeroBannerModule {
                        type
                        ...HpSharedTopHeroBannerModule_componentModule
                    }
                }
                ... on EditorialModule @include(if: $fetchInitialHomepage) {
                    type
                    ...HpSharedEditorialModule_componentModule
                }
                ... on LocationsModule {
                    type
                }
                ... on BottomBannerModule {
                    type
                    # visible used in defaultComponentModuleMap to check if the module is visible
                    # eslint-disable-next-line relay/unused-fields
                    visible
                    ...HpSharedFeaturedBannerModule_componentModule
                }
            }
        }
        ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
            ...HpSharedTopHeroBannerModule_viewer
        }
        ...HpSharedFeaturedModule_viewer
    }
`;

export const HpDefaultLayout: FC<HpDefaultLayoutProps> = ({ viewer: viewerRef }) => {
    const environment = useRelayEnvironment();
    const viewer = useFragment(viewerFragment, viewerRef);
    // Gates only the modules that cannot render on the server (browser APIs,
    // or their own suspending queries). Everything reading the SSR Relay store
    // renders on first paint.
    const isHydrated = useClientState();
    const { isMobile, isTopDisplayAdEnabled, isBottomDisplayAdEnabled, isBot } =
        useHomePageContext();

    useEffect(() => {
        trackPageview({ additional: { opt_pageURL: '/' } });
    }, []);

    useEffect(() => {
        trackUserEventHomepageView(environment);
    }, [environment]);

    useEffect(() => {
        // fireLoginHandlers runs mid-auth (before post-auth user-type selection, etc.).
        // Wait until auth/post-auth modals are fully closed before reloading
        addLoginHandlers(scheduleHomepageReloadWhenAuthIdle);
    }, []);

    useDwelledOnPage(PAGE_TYPE.HOME);

    const { homepage } = viewer;
    const { modules } = homepage || {};

    let editorialIndex = 1;

    const mods = (modules || []).map((mod, index) => {
        if (!mod) {
            return null;
        }
        const type = mod.type;

        if (!type) {
            return null;
        }

        const component = defaultComponentModuleMap[type];
        if (component) {
            return component({
                componentModule: mod,
                key: `${type}-${index}`,
                viewer,
                isMobile,
                moduleIndex: index,
                totalModules: modules?.length || 0,
                editorialModuleOrder: type === 'editorial' ? editorialIndex++ : 0,
                isHydrated,
            });
        }
        return null;
    });

    const getModuleIndex = ({
        moduleKey,
        direction = 'forward',
    }: {
        moduleKey: string;
        direction?: 'forward' | 'backward';
    }): number => {
        const moduleIndex =
            direction === 'forward'
                ? mods.findIndex(mod => (mod?.key || '').toString().includes(moduleKey))
                : mods.findLastIndex(mod => (mod?.key || '').toString().includes(moduleKey));

        if (moduleIndex === -1) {
            return mods.length - 1;
        }

        return moduleIndex;
    };

    const isOneEditorialModule =
        getModuleIndex({ moduleKey: 'editorial' }) ===
        getModuleIndex({ moduleKey: 'editorial', direction: 'backward' });

    if (!isBot) {
        mods.splice(
            0,
            0,
            <Suspense
                key="hp-new-arrivals"
                fallback={<HpRecentlyViewedShimmer isMobile={isMobile} />}
            >
                <HpNewArrivalsCarousel />
            </Suspense>
        );
    }

    mods.splice(
        getModuleIndex({ moduleKey: 'editorial' }) + 1,
        0,
        <HpRecentlySearchedItemsModuleLazy key="hp-recentlySearched" />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'hp-recentlySearched' }) + 1,
        0,
        <HpSharedItemsGrid
            key="hp-shared-items-grid"
            user={null}
            userId={null}
            fetchFavorites={false}
        />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'locations' }) + 1,
        0,
        <HpSharedFeaturedModuleLazy
            key="featured"
            viewer={viewer}
            user={null}
            isClient={isHydrated}
        />
    );

    if (isOneEditorialModule) {
        mods.splice(
            getModuleIndex({ moduleKey: 'shopBy' }),
            0,
            <HpSharedSkinnyBannerModule key="skinnyBanner" />
        );
    } else {
        mods.splice(
            getModuleIndex({ moduleKey: 'editorial', direction: 'backward' }),
            0,
            <HpSharedSkinnyBannerModule key="skinnyBanner" />
        );
    }

    if (isTopDisplayAdEnabled) {
        mods.splice(
            1, // insert after the new arrivals carousel module
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-top"
                placement={HOME_TOP_BILLBOARD_PLACEMENT}
            />
        );
    }

    if (isBottomDisplayAdEnabled) {
        mods.splice(
            getModuleIndex({ moduleKey: 'locations' }) + 1,
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-bottom"
                placement={HOME_BOTTOM_BILLBOARD_PLACEMENT}
            />
        );
    }

    return (
        <>
            {mods}
            {isHydrated && <HpSharedCompleteEmailOptinConfirmationModalLazy />}
            {isHydrated && (mobile.iPhone() || mobile.iPad()) && (
                <HpSharedAppBannerLazy user={null} />
            )}
        </>
    );
};
